import React, { useMemo, useState } from 'react';
import { PenLine, Settings } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';

export interface SignatureOption {
  label: string;
  html: string;
}

export interface ComposeSignaturesSidebarProps {
  options: SignatureOption[];
  onInsertSignature: (html: string) => void;
  onManageSignatures: () => void;
  onEditReplyForwardIntro?: () => void;
  showReplyForwardIntro: boolean;
  onClose: () => void;
}

const ComposeSignaturesSidebar: React.FC<ComposeSignaturesSidebarProps> = ({
  options,
  onInsertSignature,
  onManageSignatures,
  onEditReplyForwardIntro,
  showReplyForwardIntro,
  onClose,
}) => {
  const { colors } = useTheme();
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((opt) => opt.label.toLowerCase().includes(q));
  }, [options, query]);

  return (
    <aside
      className={cn(
        'flex flex-col h-full min-h-0 self-stretch w-[min(100vw,20rem)] flex-shrink-0 border-r',
        colors.border,
        colors.surface,
      )}
      aria-label="Signatures"
    >
      <div className={cn('flex items-center justify-between gap-1 px-1.5 py-1 border-b flex-shrink-0', colors.border)}>
        <span className={cn('text-[11px] font-semibold', colors.text)}>Signatures</span>
        <button
          type="button"
          onClick={onClose}
          className={cn('p-1 rounded', colors.textMuted, colors.interactiveHover)}
          title="Close"
          aria-label="Close signatures"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="px-1.5 py-1 flex-shrink-0">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search…"
          className={cn(
            'w-full text-[11px] rounded border px-1.5 py-1 outline-none focus:ring-1 focus:ring-indigo-500/40',
            colors.border,
            colors.surface,
            colors.text,
          )}
          aria-label="Search signatures"
        />
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-1 pb-2">
        {options.length === 0 ? (
          <p className={cn('px-1 py-2 text-[10px]', colors.textMuted)}>No signatures assigned to you.</p>
        ) : filtered.length === 0 ? (
          <p className={cn('px-1 py-2 text-[10px]', colors.textMuted)}>No matches.</p>
        ) : (
          <ul>
            {filtered.map((opt, i) => (
              <li key={`${opt.label}-${i}`} className={cn('border-b last:border-b-0 py-1.5', colors.border)}>
                <button
                  type="button"
                  onClick={() => {
                    onInsertSignature(opt.html);
                    onClose();
                  }}
                  className={cn(
                    'group w-full text-left rounded px-1 py-1 text-[11px] font-medium leading-tight transition-colors',
                    colors.text,
                    'hover:bg-amber-50 hover:text-amber-900 dark:hover:bg-amber-950/40 dark:hover:text-amber-100',
                    'focus:outline-none focus-visible:ring-1 focus-visible:ring-amber-500/50',
                  )}
                  title={`Insert ${opt.label}`}
                >
                  <span className="flex items-center gap-1.5 min-w-0">
                    <PenLine className="w-3.5 h-3.5 flex-shrink-0 text-amber-600 dark:text-amber-400" strokeWidth={1.75} aria-hidden />
                    <span className="truncate">{opt.label}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className={cn('flex-shrink-0 border-t px-1 py-1.5 space-y-0.5', colors.border)}>
        <button
          type="button"
          onClick={() => {
            onManageSignatures();
            onClose();
          }}
          className={cn(
            'w-full text-left rounded px-1.5 py-1.5 text-[11px] font-medium flex items-center gap-1.5 transition-colors',
            colors.text,
            'hover:bg-black/5 dark:hover:bg-white/5',
          )}
        >
          <Settings className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={1.75} aria-hidden />
          Manage signatures
        </button>
        {showReplyForwardIntro && onEditReplyForwardIntro && (
          <button
            type="button"
            onClick={() => {
              onEditReplyForwardIntro();
              onClose();
            }}
            className={cn(
              'w-full text-left rounded px-1.5 py-1.5 text-[11px] flex items-center gap-1.5 transition-colors',
              colors.textMuted,
              'hover:bg-black/5 dark:hover:bg-white/5 hover:text-foreground',
            )}
          >
            <PenLine className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={1.75} aria-hidden />
            Message before signature…
          </button>
        )}
      </div>
    </aside>
  );
};

export default ComposeSignaturesSidebar;
